<template>
  <div
    class="kp-upl"
    :class="{ 'kp-upl--paused': upload.paused }"
    role="status"
    aria-live="polite"
  >
    <span
      class="kp-upl__orbit"
      aria-hidden="true"
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="kp-upl__arrow"
      >
        <polyline points="17 11 12 6 7 11" />
        <line
          x1="12"
          y1="6"
          x2="12"
          y2="18"
        />
      </svg>
    </span>

    <span class="kp-upl__info">
      <span class="kp-upl__name">{{ fileName }}</span>
      <span class="kp-upl__meta">{{ statusLine }}</span>
    </span>

    <span class="kp-upl__actions">
      <button
        type="button"
        class="kp-upl__ctl"
        :aria-label="pauseLabel"
        :title="pauseLabel"
        @click="$emit(upload.paused ? 'resume' : 'pause')"
      >
        <!-- The glyph morphs between the two states rather than swapping. -->
        <transition
          name="kp-upl-icon"
          mode="out-in"
        >
          <svg
            v-if="upload.paused"
            key="resume"
            viewBox="0 0 24 24"
            width="13"
            height="13"
            fill="currentColor"
            stroke="currentColor"
            stroke-width="2"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <polygon points="9 6.5 18.5 12 9 17.5" />
          </svg>
          <svg
            v-else
            key="pause"
            viewBox="0 0 24 24"
            width="13"
            height="13"
            fill="none"
            stroke="currentColor"
            stroke-width="2.6"
            stroke-linecap="round"
            aria-hidden="true"
          >
            <line
              x1="9"
              y1="7"
              x2="9"
              y2="17"
            />
            <line
              x1="15"
              y1="7"
              x2="15"
              y2="17"
            />
          </svg>
        </transition>
      </button>

      <button
        type="button"
        class="kp-upl__ctl kp-upl__ctl--danger"
        aria-label="Cancel upload"
        title="Cancel upload"
        @click="$emit('cancel')"
      >
        <svg
          viewBox="0 0 24 24"
          width="13"
          height="13"
          fill="none"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          aria-hidden="true"
        >
          <line
            x1="7.5"
            y1="7.5"
            x2="16.5"
            y2="16.5"
          />
          <line
            x1="16.5"
            y1="7.5"
            x2="7.5"
            y2="16.5"
          />
        </svg>
      </button>
    </span>

    <span
      class="kp-upl__bar"
      aria-hidden="true"
    ><span
      class="kp-upl__bar-fill"
      :style="{ transform: `scaleX(${upload.percent / 100})` }"
    /></span>
  </div>
</template>

<script>
// KP-3919: the in-progress row for a brand-catalogue upload — animated
// marker, live percentage, pause/resume and cancel, and the progress bar.
// Both file sections render it, so it lives here rather than twice in the
// collection edit view.
import { textTruncate } from '@/@core/utils/utils'

export default {
  name: 'BrandCatalogUploadRow',
  props: {
    // { fileName, percent, paused, done, total }
    upload: { type: Object, required: true },
  },
  computed: {
    fileName() {
      return textTruncate(this.upload.fileName)
    },
    pauseLabel() {
      return this.upload.paused ? 'Resume upload' : 'Pause upload'
    },
    // "Uploading 2 of 4 · 58%" — the batch position only when there is one.
    statusLine() {
      const state = this.upload.paused ? 'Paused' : 'Uploading'
      const batch = this.upload.total > 1
        ? ` ${this.upload.done + 1} of ${this.upload.total}`
        : ''
      return `${state}${batch} · ${this.upload.percent}%`
    },
  },
}
</script>

<style lang="scss" scoped>
.kp-upl {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--v2-space-3, 0.75rem);
  padding: var(--v2-space-2, 0.5rem) var(--v2-space-3, 0.75rem);
  border: 1px solid rgba(116, 125, 238, 0.35);
  border-radius: var(--v2-radius-md, 0.5rem);
  background: rgba(116, 125, 238, 0.05);
  overflow: hidden;

  // The sweep belongs to the PAUSED state — it is the "still here, just
  // holding" signal. While bytes are actually moving the ring carries that,
  // so this stays hidden.
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      105deg,
      transparent 30%,
      rgba(255, 255, 255, 0.65) 50%,
      transparent 70%
    );
    transform: translateX(-100%);
    opacity: 0;
    pointer-events: none;
  }

  // Paused means paused: the ring and arrow freeze where they are, and the
  // sweep goes away entirely — a shimmer frozen mid-travel just looks like a
  // stray highlight. Motion here is a claim that bytes are moving.
  &--paused {
    // Freezing the conic gradient mid-spin leaves a bare wedge that reads as
    // a rendering fault. Swap it for an even, complete ring instead.
    .kp-upl__orbit::before {
      animation: none;
      background: rgba(116, 125, 238, 0.42);
    }

    .kp-upl__arrow {
      animation: none;
      opacity: 0.8;
    }

    &::after {
      opacity: 1;
      animation: kp-upl-shimmer 2.2s ease-in-out infinite;
    }
  }

  &__orbit {
    position: relative;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 10px;
    color: #6a73e8;
    background: #efeefc;

    // A conic gradient masked to a 2px ring, rotating — the "working" motif.
    &::before {
      content: "";
      position: absolute;
      inset: -3px;
      border-radius: 12px;
      padding: 2px;
      background: conic-gradient(
        from 0deg,
        transparent 0deg,
        rgba(116, 125, 238, 0.9) 120deg,
        transparent 300deg
      );
      -webkit-mask: linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      animation: kp-upl-orbit 2.4s linear infinite;
    }
  }

  &__arrow {
    animation: kp-upl-rise 1.1s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  }

  &__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__name {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--v2-foreground, #2e2c34);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__meta {
    font-size: 0.6875rem;
    color: var(--v2-muted-foreground, #6e6b7b);
    font-variant-numeric: tabular-nums;
  }

  &__actions {
    flex-shrink: 0;
    display: inline-flex;
    gap: 8px;
  }

  // Round icon controls: hover fills + lifts, :active presses in, and the
  // released press throws an expanding ring (::after) — pure CSS feedback.
  &__ctl {
    position: relative;
    width: 30px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(106, 115, 232, 0.35);
    background: #ffffff;
    color: #4f56d6;
    border-radius: 50%;
    cursor: pointer;
    transition: background 0.18s ease, color 0.18s ease,
      border-color 0.18s ease, box-shadow 0.18s ease,
      transform 0.18s cubic-bezier(0.22, 1, 0.36, 1);

    svg {
      display: block;
    }

    &::after {
      content: "";
      position: absolute;
      inset: -1px;
      border-radius: 50%;
      box-shadow: 0 0 0 9px rgba(106, 115, 232, 0);
      transition: box-shadow 0.45s ease;
      pointer-events: none;
    }

    &:hover {
      background: #6a73e8;
      border-color: #6a73e8;
      color: #ffffff;
      transform: translateY(-1px);
      box-shadow: 0 3px 10px rgba(106, 115, 232, 0.35);
    }

    &:active {
      transform: translateY(0) scale(0.86);

      &::after {
        box-shadow: 0 0 0 0 rgba(106, 115, 232, 0.4);
        transition: none;
      }
    }

    &:focus-visible {
      outline: 2px solid #6a73e8;
      outline-offset: 2px;
    }

    &--danger {
      color: #b2453d;
      border-color: rgba(226, 95, 85, 0.4);

      &::after {
        box-shadow: 0 0 0 9px rgba(201, 86, 77, 0);
      }

      &:hover {
        background: #c9564d;
        border-color: #c9564d;
        color: #ffffff;
        box-shadow: 0 3px 10px rgba(201, 86, 77, 0.35);
      }

      &:active::after {
        box-shadow: 0 0 0 0 rgba(201, 86, 77, 0.4);
      }

      &:focus-visible {
        outline-color: #c9564d;
      }
    }
  }

  &__bar {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 3px;
    background: rgba(116, 125, 238, 0.15);
    overflow: hidden;
  }

  &__bar-fill {
    position: absolute;
    inset: 0;
    border-radius: 2px;
    background: #6a73e8;
    transform-origin: left center;
    transition: transform 0.25s ease;
  }
}

// Pause <-> play morph: the outgoing glyph shrinks away with a twist, the
// incoming one springs in the same direction.
.kp-upl-icon-enter-active,
.kp-upl-icon-leave-active {
  transition: transform 0.16s ease, opacity 0.16s ease;
}

.kp-upl-icon-enter {
  opacity: 0;
  transform: scale(0.3) rotate(-60deg);
}

.kp-upl-icon-leave-to {
  opacity: 0;
  transform: scale(0.3) rotate(60deg);
}

@keyframes kp-upl-orbit {
  to { transform: rotate(360deg); }
}

@keyframes kp-upl-rise {
  0%,
  100% {
    transform: translateY(2px);
    opacity: 0.55;
  }

  50% {
    transform: translateY(-2px);
    opacity: 1;
  }
}

@keyframes kp-upl-shimmer {
  to { transform: translateX(100%); }
}

@media (prefers-reduced-motion: reduce) {
  .kp-upl__arrow,
  .kp-upl::after,
  .kp-upl--paused::after,
  .kp-upl__orbit::before { animation: none; }

  .kp-upl__ctl {
    transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;

    &::after { transition: none; }

    &:hover,
    &:active { transform: none; }
  }

  .kp-upl__bar-fill { transition: none; }

  .kp-upl-icon-enter-active,
  .kp-upl-icon-leave-active { transition: none; }
}
</style>
